import { useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CalendarClock, Send, CheckCircle, MapPin, Phone, User, Users, Upload } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const ACCEPTED_REPORTS = '.pdf,.png,.jpg,.jpeg,.webp';

function toSlots(range, slotMinutes = 30) {
  const [start, end] = String(range || '').split('-');
  if (!start || !end) return [];

  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);

  if ([sh, sm, eh, em].some((n) => Number.isNaN(n))) return [];

  const base = new Date();
  base.setHours(sh, sm, 0, 0);
  const finish = new Date();
  finish.setHours(eh, em, 0, 0);

  const slots = [];
  for (let t = new Date(base); t < finish; t = new Date(t.getTime() + slotMinutes * 60000)) {
    const hh = String(t.getHours()).padStart(2, '0');
    const mm = String(t.getMinutes()).padStart(2, '0');
    slots.push(`${hh}:${mm}`);
  }

  return slots;
}

export default function InPersonBooking() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth();

  const doctor = location.state && typeof location.state === 'object' ? location.state : null;

  const storedUser = localStorage.getItem('user');
  let userFromStorage = null;
  try {
    userFromStorage = storedUser ? JSON.parse(storedUser) : null;
  } catch (err) {
    userFromStorage = null;
  }

  const patient = profile?._id ? profile : (userFromStorage || {});
  const patientId = patient?._id || userFromStorage?._id || localStorage.getItem('userId');

  const [formData, setFormData] = useState({
    dateIndex: 0,
    selectedSlot: '',
    patientName: patient?.fullName || patient?.name || '',
    age: '',
    phone: patient?.phone || '',
    reasonForVisit: '',
    familyMemberName: '',
    familyMemberRelation: '',
    reports: [],
  });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const availableDates = useMemo(() => {
    if (!doctor?.availability) return [];

    const next14 = [];
    const now = new Date();
    for (let i = 0; i < 14; i += 1) {
      const d = new Date(now);
      d.setDate(now.getDate() + i);
      const weekday = d.toLocaleDateString(undefined, { weekday: 'long' }).toLowerCase();
      const dayRanges = doctor.availability?.[weekday] || [];
      const slots = dayRanges.flatMap((r) => toSlots(r, 30));
      if (slots.length) {
        next14.push({ date: d, slots });
      }
    }

    return next14;
  }, [doctor]);

  const inPersonFee = Number(doctor?.consultationFeeInPerson || doctor?.price || 65);
  const selectedDate = availableDates[formData.dateIndex];

  const handleField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFiles = (event) => {
    const files = Array.from(event.target.files || []);
    setFormData((prev) => ({ ...prev, reports: files.slice(0, 4) }));
  };

  const buildAppointmentISO = () => {
    if (!selectedDate || !formData.selectedSlot) return '';

    const [hh, mm] = formData.selectedSlot.split(':').map(Number);
    const dt = new Date(selectedDate.date);
    dt.setHours(hh, mm, 0, 0);
    return dt.toISOString();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!doctor?._id || !patientId) {
      alert('Missing doctor or patient details. Please try again from doctor profile.');
      return;
    }

    if (!selectedDate || !formData.selectedSlot) {
      alert('Please pick a date and time slot from availability.');
      return;
    }

    const appointmentTime = buildAppointmentISO();
    if (!appointmentTime) {
      alert('Invalid selected slot.');
      return;
    }

    const reportFiles = formData.reports.map((file) => ({
      name: file.name,
      size: file.size,
      mimeType: file.type,
    }));

    const payload = {
      patient: patientId,
      doctor: doctor._id,
      mode: 'in-person',
      consultationType: 'in-person',
      appointmentTime,
      duration: 30,
      reason: formData.reasonForVisit,
      patient_: patient,
      patientEmail: patient?.email || '',
      doctor_: {
        name: doctor.name,
        email: doctor.email,
        phone: doctor.phone,
      },
      inPersonDetails: {
        patientName: formData.patientName,
        patientAge: Number(formData.age),
        patientPhone: formData.phone,
        reasonForVisit: formData.reasonForVisit,
        familyMemberName: formData.familyMemberName,
        familyMemberRelation: formData.familyMemberRelation,
        reports: reportFiles,
      },
      clinicDetails: {
        clinicName: doctor.clinicName || 'Clinic',
        address: doctor.city || doctor.clinicName || 'Address not provided',
      },
      requestedChannels: ['dashboard', 'push', 'sms', 'whatsapp', 'email'],
    };

    setSubmitting(true);
    try {
      const response = await api.post('/api/consultations', payload);
      setConfirmation(response.data);
    } catch (err) {
      const message = err?.response?.data?.message || 'Failed to create clinic booking.';
      alert(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!doctor) {
    return (
      <div className="container py-4" style={{ maxWidth: 640 }}>
        <div className="alert alert-warning d-flex justify-content-between align-items-center" role="alert">
          <span>Doctor details missing. Please choose a doctor again.</span>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => navigate('/doctor-consult')}>
            Go to doctors
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4" style={{ maxWidth: 640 }}>
      <div className="d-flex align-items-center mb-3">
        <button
          type="button"
          className="btn btn-link p-0 me-2"
          onClick={() => navigate(-1)}
          style={{ color: 'var(--primary)', textDecoration: 'none' }}
        >
          <i className="fi fi-br-angle-left" style={{ fontSize: 20, color: 'var(--primary)' }}></i>
        </button>
        <h5 className="fw-semibold mb-0" style={{ color: 'var(--primary)' }}>Book Clinic Visit</h5>
      </div>

      <div className="card shadow-sm mb-4 p-3" style={{ borderRadius: 16 }}>
        <h6 className="fw-bold mb-1">{doctor.name}</h6>
        <p className="text-muted mb-1" style={{ fontSize: 13 }}>{doctor.specialty || 'Specialist'}</p>
        <p className="mb-1" style={{ fontSize: 13 }}><MapPin size={14} className="me-1" /> {doctor.clinicName || 'Clinic location'}</p>
        <p className="mb-0" style={{ fontSize: 13 }}><Phone size={14} className="me-1" /> {doctor.phone || 'Contact not available'}</p>
        <p className="text-success fw-medium mt-2 mb-0" style={{ fontSize: 13 }}>${inPersonFee} in-person fee</p>
      </div>

      {!confirmation ? (
        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="fw-medium mb-2 d-block">Select Date</label>
            {availableDates.length ? (
              <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6 }}>
                {availableDates.map((item, index) => (
                  <button
                    key={item.date.toISOString()}
                    type="button"
                    className="btn btn-sm"
                    onClick={() => setFormData((prev) => ({ ...prev, dateIndex: index, selectedSlot: '' }))}
                    style={{
                      borderRadius: 10,
                      minWidth: 96,
                      borderColor: formData.dateIndex === index ? 'var(--primary)' : '#ced4da',
                      background: formData.dateIndex === index ? 'var(--primary)' : '#fff',
                      color: formData.dateIndex === index ? '#fff' : '#212529',
                    }}
                  >
                    {item.date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                  </button>
                ))}
              </div>
            ) : (
              <div className="alert alert-info mb-0">No clinic slots available in the next 14 days.</div>
            )}
          </div>

          <div className="mb-4">
            <label className="fw-medium mb-2 d-block">Select Time Slot</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {(selectedDate?.slots || []).map((slot) => (
                <button
                  key={slot}
                  type="button"
                  className="btn btn-sm"
                  onClick={() => handleField('selectedSlot', slot)}
                  style={{
                    borderRadius: 999,
                    borderColor: formData.selectedSlot === slot ? 'var(--primary)' : '#ced4da',
                    background: formData.selectedSlot === slot ? 'var(--primary)' : '#fff',
                    color: formData.selectedSlot === slot ? '#fff' : '#212529',
                  }}
                >
                  {slot}
                </button>
              ))}
            </div>
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="fw-medium mb-2 d-block">Patient Name</label>
              <div className="input-group">
                <span className="input-group-text"><User size={14} /></span>
                <input
                  type="text"
                  className="form-control"
                  value={formData.patientName}
                  onChange={(e) => handleField('patientName', e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="col-md-3">
              <label className="fw-medium mb-2 d-block">Age</label>
              <input
                type="number"
                min="0"
                max="120"
                className="form-control"
                value={formData.age}
                onChange={(e) => handleField('age', e.target.value)}
                required
              />
            </div>
            <div className="col-md-3">
              <label className="fw-medium mb-2 d-block">Phone</label>
              <input
                type="tel"
                className="form-control"
                value={formData.phone}
                onChange={(e) => handleField('phone', e.target.value)}
                required
              />
            </div>
          </div>

          <div className="mb-3">
            <label className="fw-medium mb-2 d-block">Reason for Visit</label>
            <textarea
              className="form-control"
              rows={3}
              value={formData.reasonForVisit}
              onChange={(e) => handleField('reasonForVisit', e.target.value)}
              required
            />
          </div>

          <div className="row g-3 mb-3">
            <div className="col-md-6">
              <label className="fw-medium mb-2 d-block">Family Member (optional)</label>
              <div className="input-group">
                <span className="input-group-text"><Users size={14} /></span>
                <input
                  type="text"
                  className="form-control"
                  value={formData.familyMemberName}
                  onChange={(e) => handleField('familyMemberName', e.target.value)}
                />
              </div>
            </div>
            <div className="col-md-6">
              <label className="fw-medium mb-2 d-block">Relation (optional)</label>
              <input
                type="text"
                className="form-control"
                value={formData.familyMemberRelation}
                onChange={(e) => handleField('familyMemberRelation', e.target.value)}
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="fw-medium mb-2 d-block">Upload Reports (optional)</label>
            <div className="input-group">
              <span className="input-group-text"><Upload size={14} /></span>
              <input
                type="file"
                className="form-control"
                accept={ACCEPTED_REPORTS}
                multiple
                onChange={handleFiles}
              />
            </div>
            {formData.reports.length > 0 ? (
              <small className="text-muted d-block mt-2">
                Attached: {formData.reports.map((f) => f.name).join(', ')}
              </small>
            ) : null}
          </div>

          <button
            type="submit"
            className="btn w-100 py-2 fw-semibold"
            disabled={submitting || !availableDates.length}
            style={{
              borderRadius: 12,
              fontSize: 14,
              display: 'flex',
              alignItems: 'center',
              color: '#fff',
              background: 'var(--primary)',
              borderColor: 'var(--primary)',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <Send size={16} /> {submitting ? 'Booking...' : `Request Clinic Visit $${inPersonFee}`}
          </button>
        </form>
      ) : (
        <div className="card card-theme shadow-sm mb-4 p-3" style={{ borderRadius: 16 }}>
          <p className="mb-2" style={{ fontSize: 14 }}>
            <CalendarClock size={16} className="me-2" />
            Clinic booking request sent for <strong>{new Date(confirmation.appointmentTime).toLocaleString()}</strong>.
          </p>
          <p className="mb-2" style={{ fontSize: 14 }}>
            <strong>Status:</strong> {confirmation.status}
          </p>
          <p className="mb-2" style={{ fontSize: 14 }}>
            <strong>Clinic:</strong> {confirmation?.clinicDetails?.clinicName || doctor.clinicName || 'Clinic'}
          </p>
          <p className="text-muted mb-3" style={{ fontSize: 13 }}>
            Doctor will confirm this visit. You will receive notification updates.
          </p>

          <button
            className="btn w-100"
            onClick={() => navigate('/doctor-consult')}
            style={{ background: 'var(--primary)', borderColor: 'var(--primary)', color: '#fff' }}
          >
            <CheckCircle size={16} className="me-1" /> Done
          </button>
        </div>
      )}
    </div>
  );
}
